import React, { useState } from 'react';
import { XMarkIcon, CheckIcon, ScaleIcon, CreditCardIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const FeatureManagementModal = ({ isOpen, onClose, company, onSuccess }) => {
  const { updateCompanyFeatures } = useAuth();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState({
    bankReconciliation: company?.settings?.advancedFeatures?.bankReconciliation || false,
    paymentTracking: company?.settings?.advancedFeatures?.paymentTracking || false,
    periodLocking: company?.settings?.advancedFeatures?.periodLocking || false
  });

  const featureDefinitions = [
    {
      key: 'bankReconciliation',
      name: 'Bank Reconciliation',
      description: 'Advanced bank reconciliation with automatic transaction matching',
      icon: ScaleIcon,
      color: 'blue'
    },
    {
      key: 'paymentTracking',
      name: 'Payment Tracking',
      description: 'Comprehensive payment tracking and aging reports',
      icon: CreditCardIcon,
      color: 'green'
    },
    {
      key: 'periodLocking',
      name: 'Period Locking',
      description: 'Accounting period locking and closing functionality',
      icon: LockClosedIcon,
      color: 'red'
    }
  ];

  const handleFeatureToggle = (featureKey) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: !prev[featureKey]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateCompanyFeatures(company.id, features);
      toast.success('Feature permissions updated successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating features:', error);
      toast.error(error.message || 'Failed to update feature permissions');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Advanced Features
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Enable or disable advanced features for {company?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Company Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Company:</span>
                <span className="ml-2 font-medium">{company?.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Plan:</span>
                <span className="ml-2 font-medium capitalize">{company?.plan}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium capitalize">{company?.status}</span>
              </div>
              <div>
                <span className="text-gray-600">Users:</span>
                <span className="ml-2 font-medium">{company?.stats?.totalUsers || 0}</span>
              </div>
            </div>
          </div>

          {/* Feature Configuration */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 mb-4">Advanced Features</h3>
            
            {featureDefinitions.map((feature) => {
              const Icon = feature.icon;
              const isEnabled = features[feature.key];
              
              return (
                <div key={feature.key} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${
                        feature.color === 'blue' ? 'bg-blue-100' :
                        feature.color === 'green' ? 'bg-green-100' :
                        'bg-red-100'
                      }`}>
                        <Icon className={`h-5 w-5 ${
                          feature.color === 'blue' ? 'text-blue-600' :
                          feature.color === 'green' ? 'text-green-600' :
                          'text-red-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{feature.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isEnabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFeatureToggle(feature.key)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Warning Message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-amber-900">Important Notice</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Enabling or disabling these features will immediately affect what the company can access. 
                  Users already in the system will need to refresh their browser to see the changes.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Update Features
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeatureManagementModal; 